import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Progress,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Plus, Trash2 } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { checklistTemplateService, type ChecklistTemplate } from '@/lib/services/checklistTemplateService'
import { taskService, type TaskChecklistItem, type TaskChecklistProgress } from '@/lib/services/taskService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type Props = {
  taskId: number
  isDisabled?: boolean
  onProgressChange?: (progress: TaskChecklistProgress) => void
}

export default function TaskChecklist({ taskId, isDisabled = false, onProgressChange }: Props) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [items, setItems] = useState<TaskChecklistItem[]>([])
  const [progress, setProgress] = useState<TaskChecklistProgress>({
    total: 0,
    checked: 0,
    required_total: 0,
    required_checked: 0,
  })
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [newRequired, setNewRequired] = useState(false)
  const [adding, setAdding] = useState(false)
  const [library, setLibrary] = useState<ChecklistTemplate[]>([])
  const [attachId, setAttachId] = useState('')
  const [attaching, setAttaching] = useState(false)
  const [pendingReplace, setPendingReplace] = useState(false)
  const {
    isOpen: attachConfirmOpen,
    onOpen: openAttachConfirm,
    onClose: closeAttachConfirm,
  } = useDisclosure()
  const attachCancelRef = useRef<HTMLButtonElement>(null)

  const reportProgress = useCallback(
    (next: TaskChecklistProgress) => {
      setProgress(next)
      onProgressChange?.(next)
    },
    [onProgressChange],
  )

  const loadChecklist = useCallback(async () => {
    setLoading(true)
    try {
      const result = await taskService.listChecklist(taskId)
      setItems(result.items)
      reportProgress(result.progress)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [taskId, showError, reportProgress])

  useEffect(() => {
    void loadChecklist()
  }, [loadChecklist])

  useEffect(() => {
    if (isDisabled) return
    void checklistTemplateService
      .list({ per_page: 200, active: true })
      .then(({ items: list }) => setLibrary(list))
      .catch(() => {})
  }, [isDisabled])

  const progressPercent = useMemo(() => {
    if (progress.total === 0) return 0
    return Math.round((progress.checked / progress.total) * 100)
  }, [progress])

  const applyResult = (result: { items: TaskChecklistItem[]; progress: TaskChecklistProgress }) => {
    setItems(result.items)
    reportProgress(result.progress)
  }

  const handleToggle = async (item: TaskChecklistItem, checked: boolean) => {
    if (isDisabled) return
    setUpdatingId(item.id)
    try {
      const updated = await taskService.updateChecklistItem(taskId, item.id, { checked })
      setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      setProgress((prev) => {
        const wasChecked = item.checked
        const nowChecked = updated.checked
        if (wasChecked === nowChecked) return prev
        const delta = nowChecked ? 1 : -1
        const next = {
          ...prev,
          checked: prev.checked + delta,
          required_checked: item.required ? prev.required_checked + delta : prev.required_checked,
        }
        onProgressChange?.(next)
        return next
      })
    } catch (err) {
      showError(err as Error)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleAddItem = async () => {
    if (isDisabled || !newLabel.trim()) return
    setAdding(true)
    try {
      const result = await taskService.createChecklistItem(taskId, {
        label: newLabel.trim(),
        required: newRequired,
      })
      applyResult(result)
      setNewLabel('')
      setNewRequired(false)
      showSuccess('Checklist item added')
    } catch (err) {
      showError(err as Error)
    } finally {
      setAdding(false)
    }
  }

  const runAttach = async (replace: boolean) => {
    if (isDisabled || !attachId) return
    setAttaching(true)
    try {
      const result = await taskService.attachChecklistTemplate(taskId, Number(attachId), { replace })
      applyResult(result)
      setAttachId('')
      showSuccess(replace ? 'Checklist replaced' : 'Checklist attached')
      closeAttachConfirm()
    } catch (err) {
      showError(err as Error)
    } finally {
      setAttaching(false)
      setPendingReplace(false)
    }
  }

  const handleAttach = async () => {
    if (isDisabled || !attachId) return
    if (items.length > 0) {
      openAttachConfirm()
      return
    }
    await runAttach(false)
  }

  const handleRemoveItem = async (item: TaskChecklistItem) => {
    if (isDisabled) return
    setUpdatingId(item.id)
    try {
      const result = await taskService.deleteChecklistItem(taskId, item.id)
      applyResult(result)
      showSuccess(item.required ? 'Item removed (was required for this task only)' : 'Item removed from this task')
    } catch (err) {
      showError(err as Error)
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <HStack py={4} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color={colors.textSecondary}>
          Loading checklist…
        </Text>
      </HStack>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="xs" color={colors.textSecondary}>
        Checklist items belong to this task only. Attach or add what applies here; remove anything that is not
        applicable.
      </Text>
      {progress.total > 0 ? (
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="600" color={colors.textSecondary}>
              Progress
            </Text>
            <Text fontSize="xs" color={colors.textSecondary}>
              {progress.checked}/{progress.total} complete
              {progress.required_total > 0
                ? ` · ${progress.required_checked}/${progress.required_total} required`
                : ''}
            </Text>
          </HStack>
          <Progress value={progressPercent} size="sm" colorScheme="blue" borderRadius="md" />
        </Box>
      ) : (
        <Text fontSize="sm" color={colors.textSecondary}>
          No checklist items for this task yet.
        </Text>
      )}

      {!isDisabled ? (
        <VStack align="stretch" spacing={3} p={3} borderWidth="1px" borderRadius="md" borderColor={colors.borderLight}>
          <Text fontSize="sm" fontWeight="600">
            Add item
          </Text>
          <HStack align="flex-end" spacing={2}>
            <FormControl flex="1">
              <FormLabel fontSize="xs">Label</FormLabel>
              <Input
                size="sm"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Checklist item"
              />
            </FormControl>
            <Checkbox isChecked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} mb={1}>
              Required
            </Checkbox>
            <Button
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => void handleAddItem()}
              isLoading={adding}
              isDisabled={!newLabel.trim()}
            >
              Add
            </Button>
          </HStack>

          <FormControl>
            <FormLabel fontSize="xs">Attach checklist for this task…</FormLabel>
            <HStack>
              <Select
                size="sm"
                placeholder={library.length ? 'Select library checklist' : 'No checklists available'}
                value={attachId}
                onChange={(e) => setAttachId(e.target.value)}
              >
                {library.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                    {tpl.items_count != null ? ` (${tpl.items_count})` : ''}
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleAttach()}
                isLoading={attaching}
                isDisabled={!attachId}
              >
                Attach
              </Button>
            </HStack>
          </FormControl>
        </VStack>
      ) : null}

      <VStack align="stretch" spacing={2}>
        {items.map((item) => (
          <Box
            key={item.id}
            px={3}
            py={2.5}
            borderWidth="1px"
            borderRadius="md"
            borderColor={item.checked ? 'green.200' : colors.borderLight}
            bg={item.checked ? 'green.50' : colors.cardBg}
            transition="background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease"
            opacity={item.checked ? 0.85 : 1}
          >
            <HStack align="flex-start" justify="space-between" spacing={3}>
              <Checkbox
                flex="1"
                isChecked={item.checked}
                isDisabled={isDisabled || updatingId === item.id}
                onChange={(e) => void handleToggle(item, e.target.checked)}
                sx={{
                  '.chakra-checkbox__label': {
                    width: '100%',
                  },
                }}
              >
                <Text
                  fontSize="sm"
                  fontWeight={item.required && !item.checked ? '600' : '500'}
                  color={item.checked ? colors.textMuted : colors.text}
                  textDecoration={item.checked ? 'line-through' : 'none'}
                  textDecorationThickness="2px"
                  textDecorationColor="green.400"
                  transition="color 0.2s ease, text-decoration-color 0.2s ease"
                >
                  {item.label}
                  {item.required && !item.checked ? (
                    <Text as="span" color="red.500" ml={1} textDecoration="none">
                      *
                    </Text>
                  ) : null}
                </Text>
              </Checkbox>
              {!isDisabled ? (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  flexShrink={0}
                  leftIcon={<Trash2 size={12} />}
                  onClick={() => void handleRemoveItem(item)}
                  isLoading={updatingId === item.id}
                >
                  N/A
                </Button>
              ) : null}
            </HStack>
          </Box>
        ))}
      </VStack>

      <AlertDialog
        isOpen={attachConfirmOpen}
        leastDestructiveRef={attachCancelRef}
        onClose={closeAttachConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md">Attach checklist</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              This task already has {items.length} checklist item{items.length === 1 ? '' : 's'}. Append adds the
              library items alongside them. Replace removes existing items first.
            </AlertDialogBody>
            <AlertDialogFooter flexWrap="wrap" gap={2}>
              <Button ref={attachCancelRef} variant="ghost" onClick={closeAttachConfirm}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => void runAttach(false)}
                isLoading={attaching && !pendingReplace}
              >
                Append
              </Button>
              <Button
                colorScheme="orange"
                onClick={() => {
                  setPendingReplace(true)
                  void runAttach(true)
                }}
                isLoading={attaching && pendingReplace}
              >
                Replace
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  )
}
